import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { semanticTone } from 'components/@home/@settings/Tabs/Agents/agenticThemeHelpers';
import {
  attachKnowledge,
  detachKnowledge,
  findKnowledgeAttachment,
  isKnowledgeAttached,
  sanitizeKnowledgeAttachmentsForApi,
  setKnowledgeAttachmentUsage,
} from 'components/@home/@settings/Tabs/Agents/attachedKnowledgeHelpers';
import CloseIcon from 'mdi-react/CloseIcon';
import DatabaseOutlineIcon from 'mdi-react/DatabaseOutlineIcon';
import MagnifyIcon from 'mdi-react/MagnifyIcon';
import PlusIcon from 'mdi-react/PlusIcon';
import { arrayOf, bool, func, object, string } from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { I18n } from 'utils/i18n';

const getStyles = theme => {
  const primaryTone = semanticTone(theme, 'primary');
  return {
    paper: { borderRadius: '16px' },
    title: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: 700,
      padding: theme.spacing(2.5, 3, 1.5, 3),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    content: {
      padding: theme.spacing(0, 3, 0, 3),
      '&.MuiDialogContent-root': { paddingTop: 0 },
    },
    skillSelect: {
      marginBottom: theme.spacing(2),
    },
    skillSelectLabel: {
      fontSize: theme.typography.pxToRem(12),
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: theme.spacing(1),
    },
    search: {
      flex: 1,
      marginBottom: theme.spacing(2),
      '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: theme.palette.background.default,
      },
    },
    scroller: {
      maxHeight: 460,
      overflowY: 'auto',
      paddingRight: theme.spacing(0.5),
    },
    row: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      padding: theme.spacing(1.25, 1.5),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '12px',
      backgroundColor: theme.palette.background.paper,
    },
    rowHead: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5),
    },
    iconWrap: {
      width: 36,
      height: 36,
      borderRadius: '8px',
      backgroundColor: primaryTone.bg,
      color: primaryTone.fg,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    rowText: { flex: 1, minWidth: 0 },
    rowName: { fontWeight: 600, fontSize: theme.typography.pxToRem(14) },
    rowDesc: {
      fontSize: theme.typography.pxToRem(12),
      color: theme.palette.text.secondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };
};

const AttachKnowledgeDialog = ({
  open,
  onClose,
  knowledge = [],
  selectedAttachments = [],
  onSave,
  onCreateKnowledge = null,
  dialogTitle = null,
  skillOptions = null,
  selectedSkillId = '',
  onSkillChange = null,
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [local, setLocal] = useState([]);
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (open) {
      setLocal(selectedAttachments || []);
      setQuery('');
    }
  }, [open, selectedAttachments]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = [...(knowledge || [])].sort((a, b) =>
      (a.displayName || a.name).localeCompare(b.displayName || b.name),
    );
    if (!q) return list;
    return list.filter(
      k =>
        (k.name || '').toLowerCase().includes(q) ||
        (k.displayName || '').toLowerCase().includes(q) ||
        (k.description || '').toLowerCase().includes(q),
    );
  }, [knowledge, query]);

  const toggle = item => {
    const id = String(item._id);
    if (isKnowledgeAttached(local, id)) {
      setLocal(detachKnowledge(local, id));
    } else {
      setLocal(attachKnowledge(local, id));
    }
  };

  const setUsage = (knowledgeId, usage) => {
    setLocal(setKnowledgeAttachmentUsage(local, knowledgeId, usage));
  };

  const handleSave = () => {
    const norm = a => JSON.stringify(sanitizeKnowledgeAttachmentsForApi(a));
    if (norm(local) === norm(selectedAttachments)) {
      onClose();
      return;
    }
    onSave(sanitizeKnowledgeAttachmentsForApi(local));
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: styles.paper } }}
    >
      <DialogTitle sx={styles.title} component="div">
        {dialogTitle || I18n.t('AgenticChatFlow.ManageKnowledge')}
        <IconButton size="small" onClick={onClose} aria-label={I18n.t('AgenticChatFlow.Cancel')}>
          <CloseIcon size={20} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={styles.content}>
        {skillOptions?.length > 1 && onSkillChange && (
          <Box sx={styles.skillSelect}>
            <Typography sx={styles.skillSelectLabel}>
              {I18n.t('AgenticChatFlow.KnowledgeManageSkillLabel')}
            </Typography>
            <Stack
              direction="row"
              sx={{
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {skillOptions.map(skill => {
                const sid = String(skill._id);
                const selected = sid === String(selectedSkillId);
                return (
                  <Chip
                    key={sid}
                    label={skill.displayName || skill.name}
                    clickable
                    color={selected ? 'primary' : 'default'}
                    variant={selected ? 'filled' : 'outlined'}
                    onClick={() => onSkillChange(sid)}
                  />
                );
              })}
            </Stack>
          </Box>
        )}
        <TextField
          sx={styles.search}
          size="small"
          fullWidth
          placeholder={I18n.t('AgenticChatFlow.Search')}
          value={query}
          onChange={e => setQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <MagnifyIcon size={18} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Box sx={styles.scroller}>
          {filtered.map(item => {
            const id = String(item._id);
            const attached = isKnowledgeAttached(local, id);
            const att = findKnowledgeAttachment(local, id);
            return (
              <Box key={id} sx={[styles.row, { mb: 1 }]}>
                <Box sx={styles.rowHead}>
                  <Box sx={styles.iconWrap}>
                    <DatabaseOutlineIcon size={18} />
                  </Box>
                  <Box sx={styles.rowText}>
                    <Typography sx={styles.rowName}>{item.displayName || item.name}</Typography>
                    {item.description ? (
                      <Typography sx={styles.rowDesc} title={item.description}>
                        {item.description}
                      </Typography>
                    ) : null}
                  </Box>
                  <Button
                    size="small"
                    variant={attached ? 'outlined' : 'contained'}
                    color={attached ? 'inherit' : 'primary'}
                    onClick={() => toggle(item)}
                  >
                    {attached ? I18n.t('AgenticChatFlow.Detach') : I18n.t('AgenticChatFlow.Attach')}
                  </Button>
                </Box>
                {attached && (
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={2}
                    label={I18n.t('AgenticChatFlow.UsageLabel')}
                    placeholder={I18n.t('AgenticChatFlow.KnowledgeUsagePlaceholder')}
                    value={att?.usage || ''}
                    onChange={e => setUsage(id, e.target.value)}
                  />
                )}
              </Box>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0 }}>
        {onCreateKnowledge && (
          <Button
            startIcon={<PlusIcon size={16} />}
            onClick={() => {
              onClose();
              onCreateKnowledge();
            }}
            sx={{ mr: 'auto' }}
          >
            {I18n.t('AgenticChatFlow.CreateKnowledge')}
          </Button>
        )}
        <Button onClick={onClose} color="inherit">
          {I18n.t('AgenticChatFlow.Cancel')}
        </Button>
        <Button variant="contained" onClick={handleSave}>
          {I18n.t('AgenticChatFlow.Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

AttachKnowledgeDialog.propTypes = {
  open: bool.isRequired,
  onClose: func.isRequired,
  knowledge: arrayOf(object),
  selectedAttachments: arrayOf(object),
  onSave: func.isRequired,
  onCreateKnowledge: func,
  dialogTitle: string,
  skillOptions: arrayOf(object),
  selectedSkillId: string,
  onSkillChange: func,
};

export default AttachKnowledgeDialog;
